import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { SagaPayload } from '../api.types';
import {
  CreateMemberRequest,
  CreateMemberResponse,
  FindActiveProgrammesResponse,
  FlagMemberAsPaidRequest,
  FlagMemberAsPaidResponse,
  VerifyMemberStatusRequest,
  VerifyMemberStatusResponse
} from './external-registration.types';

import {
  CREATING_MEMBER,
  FINDING_ACTIVE_PROGRAMMES,
  FLAGGING_MEMBER_AS_PAID,
  VERIFYING_MEMBER_STATUS
} from './external-registration.constants';

import {
  requestCreateMember,
  requestFindActiveProgrammes,
  requestFlagMemberAsPaid,
  requestVerifyMemberStatus
} from './external-registration.actions';

import {
  getCreateMemberRequest,
  getFindActiveProgrammesRequest,
  getFlagMemberAsPaidRequest,
  getVerifyMemberStatusRequest
} from './external-registration.endpoints';

import { setMemberStatus, setProgrammes } from '../../slices/registration/registration.slice';
import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR } from '../../utils/constants';

export function* performFindActiveProgrammesRequest() {
  try {
    yield put(addSpinner(FINDING_ACTIVE_PROGRAMMES));

    const [endpoint, options] = getFindActiveProgrammesRequest();

    const { data }: AxiosResponse<FindActiveProgrammesResponse> = yield call(axios, endpoint, options);

    yield put(setProgrammes(data.programmes));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(FINDING_ACTIVE_PROGRAMMES));
  }
}

export function* watchForFindActiveProgrammesRequest() {
  yield takeLatest(requestFindActiveProgrammes.type, performFindActiveProgrammesRequest);
}

export function* performCreateMemberRequest({ payload }: SagaPayload<CreateMemberRequest, CreateMemberResponse>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(CREATING_MEMBER));

    const [endpoint, options] = getCreateMemberRequest(requestData);

    const { data }: AxiosResponse<CreateMemberResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(CREATING_MEMBER));
  }
}

export function* watchForCreateMemberRequest() {
  yield takeLatest(requestCreateMember.type, performCreateMemberRequest);
}

export function* performFlagMemberAsPaidRequest({ payload }: SagaPayload<FlagMemberAsPaidRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(FLAGGING_MEMBER_AS_PAID));

    const [endpoint, options] = getFlagMemberAsPaidRequest(requestData);

    const { data }: AxiosResponse<FlagMemberAsPaidResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(FLAGGING_MEMBER_AS_PAID));
  }
}

export function* watchForFlagMemberAsPaidRequest() {
  yield takeLatest(requestFlagMemberAsPaid.type, performFlagMemberAsPaidRequest);
}

export function* performVerifyMemberStatusRequest({ payload }: SagaPayload<VerifyMemberStatusRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(VERIFYING_MEMBER_STATUS));

    const [endpoint, options] = getVerifyMemberStatusRequest(requestData);

    const { data }: AxiosResponse<VerifyMemberStatusResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(setMemberStatus(data.status));
  } catch (error) {
    const message = getErrorMessage(error);
    if (!message.includes('No members found')) {
      yield put(addSystemNotice({ type: ERROR, message }));
    }
  } finally {
    yield put(removeSpinner(VERIFYING_MEMBER_STATUS));
  }
}

export function* watchForVerifyMemberStatusRequest() {
  yield takeLatest(requestVerifyMemberStatus.type, performVerifyMemberStatusRequest);
}

export default function* externalRegistrationSaga() {
  yield all([
    watchForFindActiveProgrammesRequest(),
    watchForCreateMemberRequest(),
    watchForFlagMemberAsPaidRequest(),
    watchForVerifyMemberStatusRequest()
  ]);
}
